import React, { useId, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { capabilitiesOf, sessionsShown, shownOnly } from "../../utils/analyticsCapabilities";
import { previousTotalOf, seriesChange } from "../../utils/analyticsSeries";
import { hourBars, toBars, toSegments, weekdayBars, weekdayLong } from "../../utils/analyticsLabels";
import { timeIn, todayIn } from "../../utils/analyticsPeriod";
import { askedForSizesShare, endingsShares, fastReplyShare, knownShare, peakOf, roundedShare, startsShares } from "../../utils/analyticsSentences";
import ChartFrame from "../charts/ChartFrame";
import RankedBars from "../charts/RankedBars";
import ResponsiveChart from "../charts/ResponsiveChart";
import StackedBar, { STACKED_BAR_HEIGHT } from "../charts/StackedBar";
import { formatCount, formatPercent, formatShare } from "../charts/format";
import BarsFigure from "./BarsFigure";
import CsatBlock from "./CsatBlock";
import EvolutionCard from "./EvolutionCard";
import KpiStrip, { KpiCell } from "./KpiStrip";
import { countText, startedWithSuggestionKpi } from "./kpis";
import Section from "./Section";
import Segmented from "./Segmented";
import SessionsBlock from "./SessionsBlock";
import VoiceBlock from "./VoiceBlock";

// O separador Conversas (spec do redesign, R7): a faixa de numeros, como comecam e como se escrevem (com
// as mensagens de clientes por origem e, num chat com voz, as mensagens de voz), o perfil do cliente (as
// conversas por perfil e as pesquisas por tamanho), a sessao iniciada (com a
// capacidade e o bloco do resumo), como acabam, os operadores num chat com operadores (com uma evolucao
// pequena) e quando.
// Cada seccao abre com uma frase de resumo feita dos proprios numeros.
const OPERATOR_METRICS = [
  { key: "handoff_requests", label: "analyticsHandoffRequests", better: "neutral" },
  { key: "handoff_requests_served", label: "analyticsOperatorServed", better: "up" },
];

// Os pedidos de operador e os atendidos vem do resumo e nao do bloco dos operadores: la fica so o que
// pede atencao.
const ConversationsStrip = ({ summary, previousComplete }) => {
  const { translate, language } = useLanguage();
  const { series, sources, endings } = summary;
  const m = series.metrics;
  const count = (value) => formatCount(value, language);
  const requests = endings.handoff_requests.outcome.total;
  const served = endings.handoff_requests.outcome.values.success || 0;
  const kpis = [
    {
      key: "started",
      label: translate("analyticsConversationsStarted"),
      value: count(sources.first_source.total),
      change: seriesChange({ series, key: "conversations_started", better: "up", previousComplete, language }),
      spark: [m.conversations_started.current, m.conversations_started.previous],
      context: translate("analyticsKpiPrevious", { previous: count(previousTotalOf(series, "conversations_started")) }),
    },
    startedWithSuggestionKpi({ summary, previousComplete, translate, language }),
    {
      key: "requests",
      needs: "operators",
      label: translate("analyticsHandoffRequests"),
      value: count(requests),
      change: seriesChange({ series, key: "handoff_requests", better: "neutral", previousComplete, language }),
      spark: [m.handoff_requests.current, m.handoff_requests.previous],
      context: translate("analyticsKpiPrevious", { previous: count(previousTotalOf(series, "handoff_requests")) }),
    },
    {
      key: "served",
      needs: "operators",
      label: translate("analyticsOperatorServed"),
      value: count(served),
      change: seriesChange({ series, key: "handoff_requests_served", better: "up", previousComplete, language }),
      spark: [m.handoff_requests_served.current, m.handoff_requests_served.previous],
      context: requests ? translate("analyticsShareOfRequests", { share: `${roundedShare(served, requests)}%` }) : null,
    },
  ];
  return <KpiStrip label={translate("analyticsHeadline")} kpis={shownOnly(kpis, capabilitiesOf(summary))} />;
};

const CompositionCard = ({ composition }) => {
  const { translate, language } = useLanguage();
  const segments = toSegments(composition, "composition", translate);
  const share = (value) => formatShare(value, composition.total, language);
  const table = {
    columns: [
      { key: "label", label: translate("analyticsCategory") },
      { key: "value", label: translate("analyticsCount"), numeric: true, format: share },
    ],
    rows: segments,
  };
  return (
    <ChartFrame title={translate("analyticsCompositionTitle")} info={translate("analyticsCompositionNote")} table={table} empty={composition.total === 0}>
      <div className="grid grid-cols-3 gap-3 mb-3">
        {segments.map((segment) => (
          <div key={segment.key} className="min-w-0">
            <p className="flex items-center gap-1.5 text-xs" style={{ color: "var(--color-text-secondary)" }}>
              <span aria-hidden="true" className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: segment.color }} />
              {segment.label}
            </p>
            <p className="text-2xl font-semibold tabular-nums" style={{ color: "var(--color-text-primary)" }}>
              {formatPercent((100 * segment.value) / composition.total, language)}
            </p>
            <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
              {countText(segment.value, "analyticsKpiConversationCount", translate, language)}
            </p>
          </div>
        ))}
      </div>
      <ResponsiveChart height={STACKED_BAR_HEIGHT}>
        {(width) => <StackedBar segments={segments} width={width} label={translate("analyticsCompositionTitle")} formatValue={share} />}
      </ResponsiveChart>
    </ChartFrame>
  );
};

const OperatorsBlock = ({ summary, period, today, previousComplete }) => {
  const { translate, language } = useLanguage();
  const headingId = useId();
  const count = (value) => formatCount(value, language);
  const { endings, series } = summary;
  const { outcome, hours, no_operator_within_hours: missed } = endings.handoff_requests;
  const inHours = hours.values.in || 0;
  const replies = endings.operator_first_reply;
  const fast = fastReplyShare(replies);
  const hoursLine = [
    translate("analyticsHoursIn", { count: count(inHours) }),
    translate("analyticsHoursOut", { count: count(hours.values.out || 0) }),
    ...(hours.unattributed > 0 ? [translate("analyticsHoursUnattributed", { count: count(hours.unattributed) })] : []),
  ].join(" · ");
  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h2 id={headingId} className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
        {translate("analyticsOperators")}
      </h2>
      <div className="rounded-xl border" style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)" }}>
        <KpiCell
          kpi={{
            key: "missed",
            label: translate("analyticsMissedInHours"),
            value: count(missed),
            context: inHours > 0 ? translate(inHours === 1 ? "analyticsMissedInHoursNoteOne" : "analyticsMissedInHoursNote", { share: `${roundedShare(missed, inHours)}%`, count: count(inHours) }) : null,
            attention: missed > 0,
          }}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-2 items-start">
        <RankedBars title={translate("analyticsHandoffOutcomeTitle")} lead={hoursLine} breakdown={outcome} dimension="outcome" />
        <BarsFigure
          title={translate("analyticsOperatorReply")}
          lead={fast === null ? null : translate("analyticsReplyTimeLead", { share: `${fast}%` })}
          info={translate("analyticsOperatorReplyNote", { waiting: count(replies.waiting) })}
          bars={toBars(replies, "bucket", translate)}
        />
      </div>
      <EvolutionCard
        series={series}
        metrics={OPERATOR_METRICS}
        title={translate("analyticsOperatorsEvolution")}
        partialLast={period.to === today}
        previousComplete={previousComplete}
        cut={summary.cut}
        timeZone={period.tz}
      />
    </section>
  );
};

// O perfil do cliente: as conversas por perfil (so num chat com o cookie, ou com dados no periodo de
// quando o cookie ainda existia) e as pesquisas por tamanho (so num chat com as variantes por tamanho, ou
// com dados no periodo de quando elas ainda existiam). Um so grafico ocupa a largura toda.
const ProfileSection = ({ summary, profile, searches }) => {
  const { translate } = useLanguage();
  const { conversations, size_searches: sizeSearches } = summary.customer_profile;
  const known = knownShare(conversations);
  const asked = askedForSizesShare(sizeSearches);
  return (
    <Section title={translate("analyticsCustomerProfile")} className={profile && searches ? "grid gap-4 lg:grid-cols-2 items-start" : "grid gap-4"}>
      {profile && (
        <RankedBars
          title={translate("analyticsConversationsByProfile")}
          lead={known === null ? null : translate("analyticsProfileLead", { share: known })}
          info={translate("analyticsCustomerProfileNote")}
          breakdown={conversations}
          dimension="profile"
        />
      )}
      {searches && (
        <RankedBars
          title={translate("analyticsSizeSearches")}
          lead={asked === null ? null : translate("analyticsSizeSearchesLead", { share: asked })}
          info={translate("analyticsSizeSearchesNote")}
          breakdown={sizeSearches}
          dimension="size_search"
        />
      )}
    </Section>
  );
};

// Os dias do horario por extenso: seguidos (com a volta ao fim de semana) dizem "de a ate b", e os outros
// listam-se.
const daysText = (days, translate, language) => {
  const name = (day) => weekdayLong(day - 1, language);
  const contiguous = days.every((day, index) => index === 0 || day === (days[index - 1] % 7) + 1);
  if (contiguous && days.length > 1) return translate("analyticsDaysRange", { from: name(days[0]), to: name(days[days.length - 1]) });
  return days.map(name).join(", ");
};

// A ultima hora do dia `day` que um corte ja cobre, na hora local do fuso: -1 com o corte ainda no dia antes
// (logo a seguir a meia-noite, com a ultima volta do rollup ontem).
const lastHourOf = (instant, day, zone) => (todayIn(zone, new Date(instant)) < day ? -1 : Number(timeIn(instant, zone, "en").slice(0, 2)));

// `lastHours` so num periodo de um dia com corte: a ultima hora com valor de cada grafico, pelo corte do grupo
// de onde a contagem vem (D8 do item 12). As horas depois dela ainda nao chegaram e vao a null, sem barra e
// com o nome sem valor, em vez de um zero que se lia "nenhuma conversa".
const WhenBlock = ({ when, hours, zone, operators, oneDay, lastHours }) => {
  const { translate, language } = useLanguage();
  const headingId = useId();
  const [chosenKind, setKind] = useState("conversations");
  // O kind guardado so vale com operadores: sem eles volta as conversas, sem rebentar.
  const kind = operators ? chosenKind : "conversations";
  const counts = when[kind];
  const data = lastHours ? { ...counts, by_hour: counts.by_hour.map((value, hour) => (hour <= lastHours[kind] ? value : null)) } : counts;
  const kindLabel = translate(kind === "conversations" ? "analyticsConversations" : "analyticsHandoffRequests");
  const dayPeak = peakOf(data.by_weekday);
  const hourPeak = peakOf(data.by_hour);
  // A faixa e a frase "fora do horario" so num chat com operadores e no fuso em que o backend avalia o
  // horario (plano, D1).
  const inZone = operators && Boolean(hours) && hours.tz === zone;
  const band = inZone ? { keys: Array.from({ length: hours.end_hour - hours.start_hour }, (_, index) => String(hours.start_hour + index)) } : null;
  const outside = inZone && hourPeak && (hourPeak.index < hours.start_hour || hourPeak.index >= hours.end_hour);
  const lead = oneDay
    ? hourPeak &&
      translate(outside ? "analyticsWhenLeadHourOutside" : "analyticsWhenLeadHour", {
        noun: translate(`analyticsWhenNoun.${kind}`),
        hour: hourPeak.index,
        hourCount: formatCount(hourPeak.value, language),
      })
    : dayPeak && hourPeak
      ? translate(outside ? "analyticsWhenLeadOutside" : "analyticsWhenLead", {
          noun: translate(`analyticsWhenNoun.${kind}`),
          day: weekdayLong(dayPeak.index, language),
          dayCount: formatCount(dayPeak.value, language),
          hour: hourPeak.index,
          hourCount: formatCount(hourPeak.value, language),
        })
      : null;
  const hourFigure = (
    <BarsFigure
      title={translate("analyticsByHour")}
      note={inZone ? translate("analyticsServiceHours", { days: daysText(hours.days, translate, language), start: hours.start_hour, end: hours.end_hour }) : null}
      bars={hourBars(data.by_hour)}
      band={band}
      downloadName={`${translate("analyticsByHour")} ${kindLabel}`}
    />
  );
  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id={headingId} className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {translate("analyticsWhen")}
        </h2>
        {operators && (
          <Segmented
            label={translate("analyticsWhenShow")}
            options={[
              { value: "conversations", label: translate("analyticsConversations") },
              { value: "handoff_requests", label: translate("analyticsHandoffRequests") },
            ]}
            value={kind}
            onChange={setKind}
          />
        )}
      </div>
      {lead && (
        <p className="text-sm" style={{ color: "var(--color-text-primary)" }}>
          {lead}
        </p>
      )}
      {oneDay ? (
        hourFigure
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <BarsFigure title={translate("analyticsByWeekday")} bars={weekdayBars(data.by_weekday, language)} downloadName={`${translate("analyticsByWeekday")} ${kindLabel}`} />
          <div className="lg:col-span-2">{hourFigure}</div>
        </div>
      )}
    </section>
  );
};

const ConversationsTab = ({
  summary,
  period,
  today,
  previousComplete,
  canOpenConversations,
  byOperator,
  byOperatorError,
  onRetryByOperator,
  customers,
  customersError,
  onRetryCustomers,
}) => {
  const { translate } = useLanguage();
  const { sources, composition, endings, when, business_hours: hours, cut } = summary;
  const starts = startsShares(sources.first_source);
  const ends = endingsShares(endings.close);
  const capabilities = capabilitiesOf(summary);
  const { operators, voice, customer_profile, size_searches } = capabilities;
  const oneDay = summary.series.granularity === "hour";
  // As conversas por hora sao do rollup, e os pedidos de operador dos contadores.
  const lastHours =
    oneDay && cut
      ? { conversations: lastHourOf(cut.conversations, period.from, period.tz), handoff_requests: lastHourOf(cut.counters, period.from, period.tz) }
      : null;
  return (
    <div className="space-y-6">
      <ConversationsStrip summary={summary} previousComplete={previousComplete} />
      <Section title={translate("analyticsHowTheyStartAndWrite")} className="grid gap-4 lg:grid-cols-2 items-start">
        <RankedBars title={translate("analyticsStarts")} lead={starts && translate(starts.voice > 0 ? "analyticsStartsLeadWithVoice" : "analyticsStartsLead", starts)} breakdown={sources.first_source} dimension="source" />
        <CompositionCard composition={composition} />
        <div className="lg:col-span-2">
          <RankedBars title={translate("analyticsMessagesBySource")} info={translate("analyticsCompositionNote")} breakdown={sources.customer_messages} dimension="source" />
        </div>
        {voice && summary.voice && (
          <div className="lg:col-span-2">
            <VoiceBlock summary={summary} period={period} previousComplete={previousComplete} canOpenConversations={canOpenConversations} />
          </div>
        )}
      </Section>
      {(customer_profile || size_searches) && <ProfileSection summary={summary} profile={customer_profile} searches={size_searches} />}
      {sessionsShown(summary) && (
        <SessionsBlock
          summary={summary}
          customers={customers}
          customersError={customersError}
          onRetry={onRetryCustomers}
          previousComplete={previousComplete}
          period={period}
          today={today}
        />
      )}
      <Section title={translate("analyticsEndingsTitle")} className="grid gap-4">
        <RankedBars title={translate("analyticsHowTheyClosed")} lead={ends && translate(operators ? "analyticsEndingsLead" : "analyticsEndingsLeadNoOperator", ends)} breakdown={endings.close} dimension="via" />
        {summary.csat && capabilities.csat && (
          <CsatBlock
            summary={summary}
            period={period}
            previousComplete={previousComplete}
            canOpenConversations={canOpenConversations}
            byOperator={byOperator}
            byOperatorError={byOperatorError}
            onRetryByOperator={onRetryByOperator}
          />
        )}
      </Section>
      {operators && <OperatorsBlock summary={summary} period={period} today={today} previousComplete={previousComplete} />}
      <WhenBlock when={when} hours={hours} zone={period.tz} operators={operators} oneDay={oneDay} lastHours={lastHours} />
    </div>
  );
};

export default ConversationsTab;
